import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { Loader2, CheckCircle2, AlertTriangle, FileSignature, Download, Eye } from "lucide-react";
import { computePricing, formatEur } from "@/lib/contract-pricing";

// 32 bytes hex → 64 chars, but historic tokens vary; accept the safe range.
const TokenSchema = z.string().trim().regex(/^[a-f0-9]{32,128}$/i, "Invalid token");

function escHtml(s: string): string {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

async function sendSignConfirmationEmail(opts: {
  to: string;
  signer_name: string;
  company_name: string | null;
  signed_at: string;
}): Promise<void> {
  const apiKey = process.env.RESEND_API_KEY;
  if (!apiKey) { console.warn("[sign] RESEND_API_KEY missing — confirmation email skipped"); return; }
  const from = "What Finland <info@whatfinland.com>";
  const replyTo = process.env.EMAIL_REPLY_TO || "info@whatfinland.com";
  const when = new Date(opts.signed_at).toLocaleString("en-GB", { timeZone: "Europe/Helsinki" });
  const html = `<!doctype html><html><body style="font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;background:#eef2f7;padding:32px 12px;margin:0;color:#0f172a">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><td align="center">
<table role="presentation" width="600" cellpadding="0" cellspacing="0" style="max-width:600px;background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 10px 40px rgba(13,27,61,.10);border:1px solid #e2e8f0">
  <tr><td style="padding:22px 28px;background:linear-gradient(135deg,#0d1b3d 0%,#13347a 55%,#0d6efd 100%);color:#fff">
    <div style="font-size:11px;letter-spacing:1.5px;text-transform:uppercase;color:#cbd5e1;font-weight:600">Contract Signed</div>
    <div style="font-size:18px;font-weight:700;margin-top:4px">What Finland Oy</div>
  </td></tr>
  <tr><td style="padding:28px 32px;line-height:1.65;font-size:15px">
    <p style="margin:0 0 12px">Hi ${escHtml(opts.signer_name)},</p>
    <p style="margin:0 0 12px">Thank you — your service agreement${opts.company_name ? ` for <strong>${escHtml(opts.company_name)}</strong>` : ""} was successfully signed on <strong>${escHtml(when)} (Helsinki time)</strong>.</p>
    <p style="margin:0 0 12px">A copy has been stored with your account. Our team will be in touch shortly to kick off onboarding.</p>
    <p style="margin:24px 0 0;font-size:12px;color:#64748b">Sent from <a href="https://whatfinland.com" style="color:#0d6efd;text-decoration:none">whatfinland.com</a> · Questions? <a href="mailto:${escHtml(replyTo)}" style="color:#0d6efd;text-decoration:none">${escHtml(replyTo)}</a></p>
  </td></tr>
</table></td></tr></table></body></html>`;
  try {
    const res = await fetch("https://api.resend.com/emails", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify({
        from,
        to: [opts.to],
        reply_to: replyTo,
        subject: `Signed: your service agreement${opts.company_name ? ` — ${opts.company_name}` : ""}`,
        html,
      }),
    });
    if (!res.ok) console.error("[sign] confirmation email failed", res.status, await res.text().catch(() => ""));
  } catch (e) {
    console.error("[sign] confirmation email error", e);
  }
}

const loadDispatchByToken = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ token: TokenSchema }).parse(d))
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row, error } = await supa
      .from("crm_contract_dispatch")
      .select("id, status, signer_name, signer_email, payload, client_id, deal_id")
      .eq("sign_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Contract not found");

    const { data: client } = await supa
      .from("crm_clients")
      .select("name, company_name, business_id")
      .eq("id", row.client_id)
      .maybeSingle();

    let payload = (row.payload ?? {}) as any;
    const { data: contractByDeal } = await supa
      .from("crm_contracts")
      .select("starts_at, ends_at, value_eur")
      .eq("deal_id", (row as any).deal_id)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    let contractRow = contractByDeal;
    if (!contractRow) {
      const { data: contractByClient } = await supa
        .from("crm_contracts")
        .select("starts_at, ends_at, value_eur")
        .eq("client_id", row.client_id)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      contractRow = contractByClient;
    }
    if (contractRow) {
      payload = {
        ...payload,
        value_eur: contractRow.value_eur ?? payload.value_eur,
        start_date: payload.start_date ?? contractRow.starts_at,
        end_date: payload.end_date ?? contractRow.ends_at,
      };
    }

    return {
      id: row.id,
      status: row.status as string,
      signer_name: row.signer_name as string | null,
      signer_email: row.signer_email as string | null,
      payload,
      company_name: (client?.company_name ?? client?.name ?? null) as string | null,
      business_id: (client?.business_id ?? null) as string | null,
      onboarding_token: null,
    };
  });

// Ensure a partner_onboarding row exists for a client (idempotent), then email the magic link.
async function ensureOnboardingInvite(
  supa: any,
  opts: {
    client_id: string;
    deal_id: string | null;
    recipient_email: string | null;
    recipient_name: string | null;
    company_name: string | null;
  },
): Promise<string | null> {
  try {
    const { data: existing } = await supa
      .from("partner_onboarding")
      .select("id, token, status")
      .eq("client_id", opts.client_id)
      .neq("status", "expired")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    let token: string | null = (existing?.token as string | undefined) ?? null;
    let onboardingId: string | null = (existing?.id as string | undefined) ?? null;

    if (!existing) {
      const { data: created, error: insErr } = await supa
        .from("partner_onboarding")
        .insert({
          client_id: opts.client_id,
          deal_id: opts.deal_id,
          recipient_email: opts.recipient_email,
          recipient_name: opts.recipient_name,
        })
        .select("id, token")
        .single();
      if (insErr) { console.error("[sign] onboarding insert failed", insErr); return null; }
      token = (created?.token as string) ?? null;
      onboardingId = (created?.id as string) ?? null;
    }

    if (!token || !opts.recipient_email || !onboardingId) return token;

    const origin = process.env.APP_URL || "https://whatfinland.com";
    const link = `${origin}/onboard/${token}`;

    try {
      const { sendOnboardingInviteEmailWithRetry } = await import(
        "@/lib/onboarding-invite-email.server"
      );
      const result = await sendOnboardingInviteEmailWithRetry({
        onboardingId,
        recipientEmail: opts.recipient_email,
        recipientName: opts.recipient_name,
        companyName: opts.company_name,
        onboardUrl: link,
      });
      if (!result.ok) {
        console.error(
          `[sign] onboarding invite email FAILED after ${result.attempts} attempts:`,
          result.error,
        );
      }
    } catch (e) {
      console.error("[sign] onboarding invite email error", e);
    }

    return token;
  } catch (e) {
    console.error("[sign] ensureOnboardingInvite error", e);
    return null;
  }
}


const signDispatchByToken = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        token: TokenSchema,
        typed_name: z.string().trim().min(2, "Please type your full name to sign.").max(200),
      })
      .parse(d),
  )
  .handler(async ({ data }) => {
    const { createClient } = await import("@supabase/supabase-js");
    const supa = createClient(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_SERVICE_ROLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    const { data: row, error } = await supa
      .from("crm_contract_dispatch")
      .select("id, status, signer_name, signer_email, deal_id, client_id")
      .eq("sign_token", data.token)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Contract not found");
    if (row.status === "signed") return { ok: true };
    if (["cancelled", "expired", "declined", "failed"].includes(row.status)) {
      throw new Error("This contract is no longer signable.");
    }
    const expected = (row as any).signer_name as string | null;
    const norm = (s: string) => s.toLowerCase().replace(/\s+/g, " ").trim();
    if (!expected || expected.trim().length < 2) {
      throw new Error("This contract has no recipient name on file. Please contact What Finland Oy.");
    }
    if (norm(expected) !== norm(data.typed_name)) {
      throw new Error(
        `Signature name doesn't match. Please type exactly: "${expected}".`,
      );
    }
    const signedAt = new Date().toISOString();
    const { error: updErr } = await supa
      .from("crm_contract_dispatch")
      .update({
        status: "signed",
        signed_at: signedAt,
        signer_name: data.typed_name.trim(),
      })
      .eq("id", row.id);
    if (updErr) throw new Error(updErr.message);

    // Propagate signed state to the canonical contract row so renewal
    // reminders and CRM UI stay in sync. Best-effort: never block signing.
    try {
      if ((row as any).deal_id) {
        await supa
          .from("crm_contracts")
          .update({ signed_at: signedAt, esign_status: "signed" })
          .eq("deal_id", (row as any).deal_id)
          .is("signed_at", null);
      } else if (row.client_id) {
        await supa
          .from("crm_contracts")
          .update({ signed_at: signedAt, esign_status: "signed" })
          .eq("client_id", row.client_id)
          .is("signed_at", null);
      }
    } catch (e) {
      console.error("[sign] failed to propagate signed_at to crm_contracts", e);
    }

    // Send confirmation email + provision onboarding invite (best-effort).
    let onboarding_token: string | null = null;
    if (row.client_id) {
      const { data: client } = await supa
        .from("crm_clients")
        .select("company_name, name, email, contact_name")
        .eq("id", row.client_id)
        .maybeSingle();
      const companyName = client?.company_name ?? client?.name ?? null;
      if ((row as any).signer_email) {
        await sendSignConfirmationEmail({
          to: (row as any).signer_email as string,
          signer_name: data.typed_name.trim(),
          company_name: companyName,
          signed_at: signedAt,
        });
      }
      onboarding_token = await ensureOnboardingInvite(supa, {
        client_id: row.client_id,
        deal_id: (row as any).deal_id ?? null,
        recipient_email: ((row as any).signer_email as string | null) ?? (client?.email as string | null) ?? null,
        recipient_name: (data.typed_name.trim() || (client?.contact_name as string | null)) ?? null,
        company_name: companyName,
      });
    }

    return { ok: true, onboarding_token };
  });

export const Route = createFileRoute("/sign/$token")({
  component: SignPage,
  head: () => ({
    meta: [{ title: "Sign Contract" }],
  }),
});

function SignPage() {
  const { token } = Route.useParams();
  const loadFn = useServerFn(loadDispatchByToken);
  const signFn = useServerFn(signDispatchByToken);
  const q = useQuery({
    queryKey: ["sign-dispatch", token],
    queryFn: () => loadFn({ data: { token } }),
    retry: false,
  });
  const [typed, setTyped] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const m = useMutation({
    mutationFn: () => signFn({ data: { token, typed_name: typed } }),
    onSuccess: () => q.refetch(),
  });

  if (q.isLoading) {
    return (
      <div className="min-h-dvh grid place-items-center bg-slate-50">
        <Loader2 className="size-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="min-h-dvh grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center bg-white rounded-2xl shadow-sm border p-8">
          <AlertTriangle className="size-8 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold">Contract unavailable</h1>
          <p className="text-sm text-slate-500 mt-1">
            {(q.error as Error)?.message ?? "This link is invalid or has expired."}
          </p>
        </div>
      </div>
    );
  }

  const d = q.data;
  const signed = d.status === "signed";

  return (
    <div className="min-h-dvh bg-slate-50 py-4 sm:py-6 px-4 lg:flex lg:items-center lg:justify-center">
      <div className="max-w-2xl w-full mx-auto bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 sm:px-8 py-4 border-b bg-gradient-to-r from-slate-900 to-slate-700 text-white relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest opacity-70">
                <FileSignature className="size-4" /> Service Agreement
              </div>
              <h1 className="text-2xl font-bold mt-1">{d.company_name ?? "Client"}</h1>
              {d.business_id && (
                <div className="text-xs opacity-80 mt-0.5">Business ID: {d.business_id}</div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <img
                src="https://whatfinland.com/icon-512.png"
                alt="What Finland Oy"
                width={40}
                height={40}
                className="rounded-lg bg-white p-1 shadow-md"
              />
              <span className="font-semibold text-sm hidden sm:inline">What Finland Oy</span>
            </div>
          </div>
        </div>

        <div className="px-6 sm:px-8 py-4 space-y-3 text-sm text-slate-700">
          <p>
            This agreement covers the social media management services described below,
            between What Finland Oy and the client named above.
          </p>
          <dl className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-4 border border-slate-100">
            {(() => {
              const { total, months, monthly, breakdown } = computePricing(d.payload ?? {});
              const start = d.payload?.start_date ?? d.payload?.starts_at ?? null;
              const end = d.payload?.end_date ?? d.payload?.ends_at ?? null;
              const term = String(d.payload?.deal_type ?? "monthly").toLowerCase();
              const fmt = (s: any) =>
                s
                  ? new Date(s).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : null;
              const lengthLabel =
                months > 0
                  ? `${months} month${months === 1 ? "" : "s"}`
                  : start && end
                    ? `${fmt(start)} → ${fmt(end)}`
                    : "—";
              return (
                <>
                  {monthly !== null && (
                    <div className="col-span-2 rounded-md border border-blue-200 bg-blue-50 p-3">
                      <dt className="text-[11px] uppercase tracking-wider text-blue-700">
                        Monthly fee
                      </dt>
                      <dd className="font-bold text-slate-900 text-lg">
                        {formatEur(monthly)}{" "}
                        <span className="text-xs font-medium text-slate-600">/ month</span>
                      </dd>
                    </div>
                  )}
                  <div className="col-span-2 rounded-md border border-slate-200 bg-white p-3">
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500">
                      Total contract value
                    </dt>
                    <dd className="font-bold text-slate-900 text-lg">{formatEur(total)}</dd>
                    {breakdown && (
                      <dd className="text-xs font-medium text-slate-500 mt-1">
                        Monthly fee × months: {breakdown}
                      </dd>
                    )}
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500">Billing</dt>
                    <dd className="font-semibold text-slate-900 capitalize">{term}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wider text-slate-500">
                      Contract length
                    </dt>
                    <dd className="font-semibold text-slate-900">{lengthLabel}</dd>
                  </div>
                </>
              );
            })()}
            <div className="col-span-2">
              <dt className="text-[11px] uppercase tracking-wider text-slate-500">Includes</dt>
              <dd className="text-slate-900 mt-2">
                {(() => {
                  const inc: any = d.payload?.includes;
                  if (!inc) return <span className="text-slate-400">—</span>;
                  if (typeof inc === "string") return inc;
                  if (typeof inc?.description === "string" && !inc.items) return inc.description;
                  const items: any[] = Array.isArray(inc) ? inc : Array.isArray(inc?.items) ? inc.items : [];
                  if (!items.length) return <span className="text-slate-400">—</span>;
                  return (
                    <ul className="space-y-1.5">
                      {items.map((it, i) => {
                        const name = it.product ?? it.name ?? it.title ?? "Item";
                        const qty = it.quantity ?? it.qty ?? 1;
                        const period = it.period ? ` / ${String(it.period).replace(/^per\s+/i, "")}` : "";
                        return (
                          <li key={i} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-md px-3 py-2">
                            <span className="font-medium text-slate-900">{name}</span>
                            <span className="text-xs font-semibold text-slate-600 tabular-nums">
                              {qty}×{period}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </dd>
            </div>
          </dl>
          <div className="rounded-lg border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-4">
            <div className="flex items-start gap-3">
              <img
                src="https://whatfinland.com/icon-512.png"
                alt="what-platform"
                width={36}
                height={36}
                className="rounded-md bg-white p-1 border border-blue-100 shrink-0"
              />
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-sm font-bold text-slate-900">what-platform</h3>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-blue-700 bg-blue-100 rounded-full px-2 py-0.5">
                    Included
                  </span>
                </div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">
                  Your business is published on{" "}
                  <a href="https://whatfinland.com" className="font-semibold text-blue-700 hover:underline">
                    whatfinland.com
                  </a>{" "}
                  — discovery listing, profile page, photo gallery, contact details, and inclusion
                  in city &amp; category guides for the full contract term.
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1 print:hidden">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 underline underline-offset-2"
            >
              <Eye className="size-3.5" /> Preview Full Contract
            </button>
            {signed && (
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5"
              >
                <Download className="size-3.5" /> Print / Save as PDF
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            By typing your full name below and clicking "Sign", you agree to be electronically
            bound to this agreement. A copy will be emailed to {d.signer_email ?? "you"}.
          </p>
        </div>

        <div className="px-6 sm:px-8 py-4 border-t bg-slate-50">
          {signed ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                <CheckCircle2 className="size-5" /> Signed. Thank you — your services are now active.
              </div>
              {m.data?.onboarding_token && d.signer_email && (
                <p className="text-sm text-slate-600 print:hidden">
                  We've emailed your personal onboarding link to <span className="font-semibold text-slate-800">{d.signer_email}</span>. Check your inbox to continue setting up your listing.
                </p>
              )}
            </div>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                m.mutate();
              }}
              className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3"
            >
              <div className="flex-1">
                <label htmlFor="typed" className="block text-xs uppercase tracking-wider text-slate-500 mb-1">
                  Type your full name to sign
                </label>
                <input
                  id="typed"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={d.signer_name ?? "Full legal name"}
                  className="w-full h-11 px-3 rounded-lg border border-slate-300 bg-white font-serif text-lg"
                />
                {d.signer_name && (
                  <p className="text-[11px] text-slate-500 mt-1">
                    Must match the recipient name on file:{" "}
                    <span className="font-semibold text-slate-700">{d.signer_name}</span>
                  </p>
                )}
                {d.signer_name &&
                  typed.trim().length >= 2 &&
                  d.signer_name.toLowerCase().replace(/\s+/g, " ").trim() !==
                    typed.toLowerCase().replace(/\s+/g, " ").trim() && (
                    <p className="text-[11px] text-amber-600 mt-1">
                      Name doesn't match — please type it exactly as shown above.
                    </p>
                  )}
              </div>
              <button
                type="submit"
                disabled={
                  m.isPending ||
                  typed.trim().length < 2 ||
                  (!!d.signer_name &&
                    d.signer_name.toLowerCase().replace(/\s+/g, " ").trim() !==
                      typed.toLowerCase().replace(/\s+/g, " ").trim())
                }
                className="h-11 px-5 rounded-lg bg-slate-900 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2"
              >
                {m.isPending && <Loader2 className="size-4 animate-spin" />} Sign Contract
              </button>
            </form>
          )}
          {m.isError && (
            <p className="text-xs text-red-600 mt-2">{(m.error as Error).message}</p>
          )}
        </div>
      </div>

      {previewOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 print:hidden"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-base font-bold">Full Contract Preview</h2>
              <button
                onClick={() => setPreviewOpen(false)}
                className="text-xs text-slate-500 hover:text-slate-900"
              >
                Close
              </button>
            </div>
            <div className="p-6 space-y-4 text-sm text-slate-700">
              <FullContractView d={d} />
            </div>
          </div>
        </div>
      )}

      <style>{`@media print {
        body { background: white !important; }
        .print\\:hidden { display: none !important; }
      }`}</style>
    </div>
  );
}

function FullContractView({ d }: { d: any }) {
  const { total, months, monthly, breakdown } = computePricing(d.payload ?? {});
  const start = d.payload?.start_date ?? d.payload?.starts_at ?? null;
  const end = d.payload?.end_date ?? d.payload?.ends_at ?? null;
  const fmt = (s: any) =>
    s ? new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
  const lengthLabel =
    months > 0 ? `${months} month${months === 1 ? "" : "s"}` : start && end ? `${fmt(start)} → ${fmt(end)}` : "—";
  const inc: any = d.payload?.includes;
  const items: any[] = Array.isArray(inc) ? inc : Array.isArray(inc?.items) ? inc.items : [];

  const Row = ({ k, v }: { k: string; v: any }) => (
    <div className="grid grid-cols-3 gap-3 py-2 border-b border-slate-100">
      <dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt>
      <dd className="col-span-2 font-medium text-slate-900">{v ?? "—"}</dd>
    </div>
  );

  return (
    <>
      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">1. Parties</h3>
        <dl>
          <Row k="Service Provider" v="What Finland Oy" />
          <Row k="Client" v={d.company_name ?? "—"} />
          <Row k="Business ID" v={d.business_id ?? "—"} />
          <Row k="Recipient Name" v={d.signer_name ?? "—"} />
          <Row k="Recipient Email" v={d.signer_email ?? "—"} />
        </dl>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">2. Commercial Terms</h3>
        <dl>
          {monthly !== null && <Row k="Monthly Fee" v={`${formatEur(monthly)} / month`} />}
          <Row k="Total Contract Value" v={formatEur(total)} />
          {breakdown && <Row k="Monthly Fee × Months" v={breakdown} />}
          <Row k="Currency" v={d.payload?.currency ?? "EUR"} />
          <Row k="Term Type" v={String(d.payload?.deal_type ?? "monthly")} />
          <Row k="Contract Length" v={lengthLabel} />
          <Row k="Start Date" v={fmt(start)} />
          <Row k="End Date" v={fmt(end)} />
        </dl>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">3. Included Services</h3>
        {items.length ? (
          <ul className="space-y-1.5">
            {items.map((it, i) => (
              <li key={i} className="flex items-center justify-between bg-slate-50 rounded px-3 py-2">
                <span className="font-medium">{it.product ?? it.name ?? it.title ?? "Item"}</span>
                <span className="text-xs font-semibold text-slate-600">
                  {it.quantity ?? it.qty ?? 1}× {it.period ? `/ ${String(it.period).replace(/^per\s+/i, "")}` : ""}
                </span>
              </li>
            ))}
          </ul>
        ) : typeof inc === "string" ? (
          <p>{inc}</p>
        ) : (
          <p className="text-slate-400">—</p>
        )}
      </section>

      <section className="rounded-lg border border-blue-100 bg-blue-50/40 p-4">
        <h3 className="text-sm font-bold text-slate-900 mb-1">4. what-platform</h3>
        <p className="text-xs text-slate-700 leading-relaxed">
          The client's business is published on <strong>whatfinland.com</strong> for the full contract
          term — discovery listing, dedicated profile page, photo gallery, contact details, and
          inclusion in relevant city &amp; category guides.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-900 mb-2">5. Signature</h3>
        <p className="text-xs text-slate-600">
          Signed electronically by typing the recipient's full legal name on this page. The typed
          name is validated server-side against the recipient name on file. Status:{" "}
          <strong className="capitalize">{d.status}</strong>.
        </p>
      </section>
    </>
  );
}
